import { useState, useEffect, useContext, useCallback } from "react";
import { brandMappingV2 } from "~src/api/brandMapping";
import { BrandMappingContextV2 } from "~src/contexts/BrandMappingContextV2";
import { COUNTRIES, CountryType } from "~src/data/countries";
import { mappingCountries } from "~src/data/mappingCounties";
import { FullBrand } from "~src/types/brand";
import { GeographicalSummaryData, MapOfBrands } from "~src/types/brandMapping";

export type FullBrandLoadContext = {
  fullBrand: FullBrand | null;
  loading: boolean;
  error: string | null;
};

export type BrandMappingFilters = {
  brand_country?: string[];
  ad_country?: string[];
  product_price_range?: string;
  estimated_monthly_traffic?: string;
  estimated_monthly_revenue?: string;
  social_followers?: string;
  has_running_ads?: boolean;
};

const useBrandMappingV2 = (
  searchText: string | undefined,
  exactMatch: boolean,
  brandToRemove: MapOfBrands[] | undefined,
) => {
  const [loadingV2, setLoading] = useState<boolean>(false);
  const [initialLoadingV2, setInitialLoading] = useState<boolean>(false);

  const {
    mapOfBrandsContext,
    setMapOfBrandsContext,
    mapOfBrandsFullResponseContext,
    setMapOfBrandsFullResponseContext,
  } = useContext(BrandMappingContextV2) || {};

  const [mappedBrandsV2, setMappedBrands] = useState<MapOfBrands[] | undefined>(
    mapOfBrandsContext
  );
  const [geographicalSummaryV2, setGeographicalSummary] =
    useState<GeographicalSummaryData>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [errorV2, setError] = useState<string | null>(null);
  const [filtersV2, setFiltersV2] = useState<BrandMappingFilters>();

  const itemsPerPage = 20;

  // Wrap filterMapOfBrands in useCallback so its reference remains stable
  const filterMapOfBrands = useCallback((data: MapOfBrands[]) => {
    let isBrandCountryMatch: boolean | undefined = true;
    let isAdCountryMatch: boolean | undefined = true;
    let isPriceInRange: boolean | undefined = true;
    let isTrafficInRange: boolean | undefined = true;
    let isRevenueInRange: boolean | undefined = true;
    let isSocialFollowersInRange: boolean | undefined = true;
    let hasRunningAds: boolean | undefined = true;

    return data.filter((brandObj) => {
      // ---------------------- Brand country filter -----------------------
      if (filtersV2?.brand_country) {
        isBrandCountryMatch = filtersV2.brand_country.includes(
          brandObj.brand.country
        );
      }
      // -------------------- Ad filter (has_running_ads) ---------------------
      if (filtersV2?.has_running_ads === true) {
        hasRunningAds = brandObj.ads.length > 0;
      }
      // ------------------------ Ad country filter --------------------------
      const adCountryFilter = filtersV2?.ad_country ?? [];
      if (adCountryFilter.length > 0) {
        isAdCountryMatch = brandObj.ads.some(
          (ad) =>
            ad &&
            ad.ad_country &&
            ad.ad_country.length > 0 &&
            ad.ad_country.some((country) => adCountryFilter.includes(country))
        );
      }
      // ---------------------- Product Price Range filter -------------------
      if (filtersV2?.product_price_range) {
        const [min, max] = filtersV2.product_price_range.split("__").map(Number);
        const products = brandObj.products;
        isPriceInRange = products.some(
          (product) => product.min_price >= min && product.max_price <= max
        );
      }
      // -------------------- Estimated monthly revenue filter ----------------
      if (filtersV2?.estimated_monthly_revenue) {
        const [min, max] = filtersV2.estimated_monthly_revenue
          .split("__")
          .map(Number);
        const revenue = brandObj.brand.store_data.estimated_monthly_revenue;
        isRevenueInRange = revenue >= min && revenue <= max;
      }
      // -------------------- Estimated monthly traffic filter ----------------
      if (filtersV2?.estimated_monthly_traffic) {
        const [min, max] = filtersV2.estimated_monthly_traffic
          .split("__")
          .map(Number);
        const traffic = brandObj.brand.store_data.estimated_monthly_traffic;
        isTrafficInRange = traffic >= min && traffic <= max;
      }
      // -------------------- Social followers count filter -------------------
      if (filtersV2?.social_followers) {
        const [min, max] = filtersV2.social_followers.split("__").map(Number);
        const totalFollowers =
          brandObj.brand.latest_fblikes + brandObj.brand.latest_instafollowers;
        isSocialFollowersInRange =
          totalFollowers >= min && totalFollowers <= max;
      }
      // Return true if all conditions are satisfied
      return (
        isBrandCountryMatch &&
        hasRunningAds &&
        isAdCountryMatch &&
        isPriceInRange &&
        isTrafficInRange &&
        isRevenueInRange &&
        isSocialFollowersInRange
      );
    });
  }, [filtersV2]);

  // Wrap filterBrandsByCountries in useCallback (no dependencies needed)
  const filterBrandsByCountries = useCallback((brands: MapOfBrands[]) => {
    return brands.filter((brand) =>
      Object.prototype.hasOwnProperty.call(mappingCountries, brand.brand.country)
    );
  }, []);

  // Wrap aggregateCountries in useCallback
  const aggregateCountries = useCallback(
    (data: MapOfBrands[]): GeographicalSummaryData | undefined => {
      // Filter the countries with the predefined mapping
      data = filterBrandsByCountries(data);

      // Helper: Count occurrences in a map
      const countOccurrences = (map: Map<string, number>, country: string) => {
        map.set(country, (map.get(country) || 0) + 1);
      };

      // Helper: Add traffic to a map
      const addTraffic = (
        map: Map<string, number>,
        country: string,
        traffic: number
      ) => {
        map.set(country, (map.get(country) || 0) + traffic);
      };

      // Initialize maps for counts and traffic
      const adCountryMap = new Map<string, number>();
      const brandCountryMap = new Map<string, number>();
      const shippingCountryMap = new Map<string, number>();
      const trafficCountryMap = new Map<string, number>();

      // Populate the maps
      data.forEach((item) => {
        // Brand's primary country
        if (item.brand?.country) {
          countOccurrences(brandCountryMap, item.brand.country);
          const lastMonthTraffic =
            item.brand.web_traffic_growth?.last_month_traffic;
          if (lastMonthTraffic) {
            addTraffic(trafficCountryMap, item.brand.country, lastMonthTraffic);
          }
        }
        // Shipping countries
        item?.brand?.shipping_to_countries?.forEach((country) =>
          countOccurrences(shippingCountryMap, country)
        );
        // Ad countries
        item?.ads?.forEach((ad) =>
          ad?.ad_country?.forEach((country) =>
            countOccurrences(adCountryMap, country)
          )
        );
      });

      // Helper to convert a Map to a sorted array of [country, count] tuples
      const mapToSortedArray = (map: Map<string, number>): [string, number][] =>
        Array.from(map)
          .sort((a, b) => b[1] - a[1])
          .map(([country, count]) => [country, count]);

      return {
        top_ad_countries: mapToSortedArray(adCountryMap).slice(0, 30),
        top_brand_countries: mapToSortedArray(brandCountryMap).slice(0, 30),
        top_shipping_countries: mapToSortedArray(shippingCountryMap).slice(0, 30),
        top_traffic_countries: mapToSortedArray(trafficCountryMap).slice(0, 30),
      };
    },
    [filterBrandsByCountries]
  );

  /**
   * Extracts unique countries from the full response.
   */
  const extractCountries = () => {
    const uniqueBrandCountries = Array.from(
      new Set(
        mapOfBrandsFullResponseContext?.map((item) => item.brand.country)
      )
    ).filter((country) => country !== null && country !== undefined);

    const formatCountryList = (countries: string[]): CountryType[] => {
      return countries.map((countryCode) => {
        const countryMatch = COUNTRIES.find((c) => c.code === countryCode);
        return {
          code: countryCode,
          label: countryMatch ? countryMatch.label : countryCode.toUpperCase(),
        };
      });
    };

    return {
      brandCountries: formatCountryList(uniqueBrandCountries),
    };
  };

  useEffect(() => {
    if (!searchText) return;

    const fetchMapping = async () => {
      try {
        setInitialLoading(true);
        setLoading(true);
        setError(null);
        const modifiedSearchString = exactMatch
          ? `"${searchText}"`
          : searchText;

        const response = await brandMappingV2(
          modifiedSearchString,
          1,
          brandToRemove?.map((brand) => brand.brand.brand_id)
        );

        if (setMapOfBrandsFullResponseContext) {
          setMapOfBrandsFullResponseContext(response.data);
        }

        let mapping = Array.isArray(response.data) ? response.data : [];
        mapping = filterMapOfBrands(mapping);
        const geographicalSummary = aggregateCountries(mapping);

        setMappedBrands(mapping);
        setGeographicalSummary(geographicalSummary);
        setCurrentPage(1);
      } catch (error: any) {
        setError(error.message);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };

    fetchMapping();
  }, [
    searchText,
    exactMatch,
    brandToRemove,
    setMapOfBrandsFullResponseContext,
    filterMapOfBrands,
    aggregateCountries,
  ]);

  useEffect(() => {
    const applyFilters = () => {
      if (
        mapOfBrandsFullResponseContext &&
        mapOfBrandsFullResponseContext.length > 0
      ) {
        setInitialLoading(true);
        const filteredMapping = filterMapOfBrands(mapOfBrandsFullResponseContext);
        const geographicalSummary = aggregateCountries(filteredMapping);

        if (setMapOfBrandsContext) {
          setMapOfBrandsContext(filteredMapping);
        }
        setGeographicalSummary(geographicalSummary);
        setCurrentPage(1);
        setInitialLoading(false);
      }
    };
    applyFilters();
  }, [
    filtersV2?.brand_country,
    filtersV2?.has_running_ads,
    filtersV2?.ad_country,
    filtersV2?.product_price_range,
    filtersV2?.estimated_monthly_revenue,
    filtersV2?.estimated_monthly_traffic,
    filtersV2?.social_followers,
    mapOfBrandsFullResponseContext,
    filterMapOfBrands,
    aggregateCountries,
    setMapOfBrandsContext,
  ]);

  useEffect(() => {
    if (!setMapOfBrandsContext) return; // Ensure context setter is available

    const paginatedBrands = Array.isArray(mappedBrandsV2)
      ? mappedBrandsV2.slice(0, currentPage * itemsPerPage)
      : [];

    setMapOfBrandsContext(paginatedBrands);
  }, [mappedBrandsV2, currentPage, itemsPerPage, setMapOfBrandsContext]);

  const loadMoreV2 = () => {
    if (
      mapOfBrandsContext &&
      currentPage * itemsPerPage >= mapOfBrandsContext.length
    )
      return;
    setCurrentPage((prev) => prev + 1);
  };

  return {
    loadMoreV2,
    geographicalSummaryV2,
    filtersV2,
    setFiltersV2,
    loadingV2,
    initialLoadingV2,
    errorV2,
    brandCountriesV2: extractCountries().brandCountries,
  };
};

export default useBrandMappingV2;
